/**
 * F5.3 — Offline outbox: kiadás / napló / fotó meta IndexedDB-be,
 * visszatérő kapcsolatnál Idempotency-Key-vel szinkron.
 */
import { get, set, del } from "idb-keyval";
import { createExpense } from "@/lib/api/expenses";
import { upsertSiteDailyLogByDate, appendToSiteDailyLog } from "@/lib/api/siteDailyLogs";
import { createDocument, uploadDocumentToStorage } from "@/lib/api/documents";

const OUTBOX_KEY = "takacs-offline-outbox-v1";

export type OutboxItem =
  | {
      id: string;
      kind: "expense";
      idempotencyKey: string;
      createdAt: string;
      payload: {
        title: string;
        amount: number;
        date: string;
        category: string;
        notes?: string | null;
        paid_by?: "Közösen" | "Réka" | "Péter" | "TPT Brány" | null;
        supplier_name?: string | null;
        amount_basis?: "gross" | "net" | "black";
        amount_net?: number;
        amount_gross?: number;
        vat_rate?: number;
        vat_amount?: number;
        vat_code?: string;
        phase_id?: string | null;
        task_id?: string | null;
        supplier_contact_id?: string | null;
        quote_id?: string | null;
        invoice_number?: string | null;
      };
    }
  | {
      id: string;
      kind: "naplo";
      idempotencyKey: string;
      createdAt: string;
      payload: {
        log_date: string;
        work_summary?: string | null;
        issues?: string | null;
        weather_summary?: string | null;
      };
    }
  | {
      id: string;
      kind: "photo";
      idempotencyKey: string;
      createdAt: string;
      payload: {
        title: string;
        /** base64 without data: prefix */
        base64: string;
        mime: string;
        fileName: string;
      };
    };

async function load(): Promise<OutboxItem[]> {
  return (await get<OutboxItem[]>(OUTBOX_KEY)) ?? [];
}

async function save(items: OutboxItem[]): Promise<void> {
  await set(OUTBOX_KEY, items);
}

export async function listOutbox(): Promise<OutboxItem[]> {
  return load();
}

export async function enqueueOutbox(
  item: Omit<OutboxItem, "id" | "createdAt" | "idempotencyKey"> & { idempotencyKey?: string }
): Promise<OutboxItem> {
  const full = {
    ...item,
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    idempotencyKey: item.idempotencyKey ?? `outbox-${crypto.randomUUID()}`
  } as OutboxItem;
  const items = await load();
  items.push(full);
  await save(items);
  return full;
}

export async function removeOutboxItem(id: string): Promise<void> {
  const items = (await load()).filter((i) => i.id !== id);
  await save(items);
}

function base64ToFile(base64: string, fileName: string, mime: string): File {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], fileName, { type: mime });
}

async function flushOne(item: OutboxItem): Promise<void> {
  if (item.kind === "expense") {
    const p = item.payload;
    await createExpense({
      title: p.title,
      amount: p.amount,
      date: p.date,
      category: p.category as "material" | "labor" | "service" | "permit" | "equipment" | "other",
      notes: p.notes ?? null,
      paid_by: p.paid_by ?? null,
      ...(p.supplier_name !== undefined ? { supplier_name: p.supplier_name } : {}),
      ...(p.amount_basis ? { amount_basis: p.amount_basis } : {}),
      ...(p.amount_net != null ? { amount_net: p.amount_net } : {}),
      ...(p.amount_gross != null ? { amount_gross: p.amount_gross } : {}),
      ...(p.vat_rate != null ? { vat_rate: p.vat_rate } : {}),
      ...(p.vat_amount != null ? { vat_amount: p.vat_amount } : {}),
      ...(p.vat_code ? { vat_code: p.vat_code } : {}),
      ...(p.phase_id !== undefined ? { phase_id: p.phase_id } : {}),
      ...(p.task_id !== undefined ? { task_id: p.task_id } : {}),
      ...(p.supplier_contact_id !== undefined ? { supplier_contact_id: p.supplier_contact_id } : {}),
      ...(p.quote_id !== undefined ? { quote_id: p.quote_id } : {}),
      ...(p.invoice_number !== undefined ? { invoice_number: p.invoice_number } : {})
    });
    return;
  }
  if (item.kind === "naplo") {
    const p = item.payload;
    if (p.work_summary || p.issues) {
      await appendToSiteDailyLog({
        logDate: p.log_date,
        ...(p.work_summary ? { workSummary: p.work_summary } : {}),
        ...(p.issues ? { issues: p.issues } : {}),
        ...(p.weather_summary ? { weatherSummary: p.weather_summary } : {})
      });
    } else {
      await upsertSiteDailyLogByDate({
        log_date: p.log_date,
        weather_summary: p.weather_summary ?? null,
        work_summary: p.work_summary ?? null,
        issues: p.issues ?? null
      });
    }
    return;
  }
  if (item.kind === "photo") {
    const p = item.payload;
    const file = base64ToFile(p.base64, p.fileName, p.mime);
    const path = await uploadDocumentToStorage(file);
    await createDocument({ title: p.title, category: "photo", file_url: path });
  }
}

/** Online visszatéréskor: FIFO flush, sikeres sor törlése. Duplikáció: Idempotency-Key. */
export async function flushOutbox(): Promise<{ ok: number; failed: number }> {
  if (typeof navigator !== "undefined" && !navigator.onLine) return { ok: 0, failed: 0 };
  const items = await load();
  let ok = 0;
  let failed = 0;
  const remaining: OutboxItem[] = [];
  for (const item of items) {
    try {
      await flushOne(item);
      ok += 1;
    } catch {
      failed += 1;
      remaining.push(item);
    }
  }
  await save(remaining);
  return { ok, failed };
}

export async function clearOutbox(): Promise<void> {
  await del(OUTBOX_KEY);
}
